import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, LinearProgress, Tooltip, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";

import type { DiagramSensorOverlay, IndoorTemperatureSummary, Sensor } from "../shared/types";
import { HeatingDiagram } from "./HeatingDiagram";

// --- Internal types ---

type SensorDataElementType = {
  sensor: Sensor;
  key: string;
  timestamp: string | undefined;
  reading: number | null | undefined;
  x: number;
  y: number;
  label: string;
  type: string;
};

// --- Helper components ---

const hasValidReading = (reading: number | null | undefined): reading is number =>
  reading !== null && reading !== undefined && !isNaN(reading);

const SensorDataElement = ({ sensorData }: { sensorData: SensorDataElementType }) => {
  const { t } = useTranslation(["propai_properties", "propai"]);
  const theme = useTheme();

  const tooltipContent = useMemo(() => {
    if (sensorData.type === "radiator_system_supply_temp_setpoint") {
      const validReading = hasValidReading(sensorData.reading);
      return (
        <Box display="flex" flexDirection="column">
          <Typography variant="labelLarge" display="block">
            {sensorData.label}
          </Typography>
          {validReading && (
            <Typography variant="bodyMedium">{sensorData.reading.toFixed(1)}°C</Typography>
          )}
        </Box>
      );
    }

    const validReading = hasValidReading(sensorData.reading);
    const readingText = validReading ? sensorData.reading.toFixed(1) : "N/A";

    return (
      <Box display="flex" flexDirection="column">
        <Typography variant="labelLarge" display="block">
          {sensorData.label}
        </Typography>
        <Typography variant="bodyMedium">{readingText}°C</Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("sensor_id")}:
        </Typography>
        <Typography variant="bodyMedium">{sensorData.sensor.external_id}</Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          Timestamp:
        </Typography>
        <Typography variant="bodyMedium">
          {sensorData.timestamp
            ? DateTime.fromISO(sensorData.timestamp).toLocaleString(DateTime.DATETIME_MED)
            : "No timestamp"}
        </Typography>
        {!validReading && (
          <Typography
            variant="bodyMedium"
            display="block"
            sx={{ color: theme.palette.warning.main }}
            mt={0.5}
          >
            No recent data
          </Typography>
        )}
      </Box>
    );
  }, [sensorData, t, theme]);

  return (
    <Box
      sx={{
        position: "absolute",
        left: `${sensorData.x}px`,
        top: `${sensorData.y}px`,
        backgroundColor: "transparent",
        borderRadius: "4px",
        border: `1px solid ${theme.palette.outline?.main ?? "#74777F"}`,
        transform: "translate(-50%, -50%)",
        zIndex: 2,
        cursor: "pointer",
      }}
      p={1}
    >
      <Tooltip
        title={tooltipContent}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: { backgroundColor: theme.palette.inverseSurface },
          },
        }}
      >
        <Typography
          variant="bodyMedium"
          sx={{
            color: hasValidReading(sensorData.reading) ? "text.primary" : "text.secondary",
          }}
        >
          {hasValidReading(sensorData.reading) ? sensorData.reading.toFixed(1) : "N/A"}
          {"°C"}
        </Typography>
      </Tooltip>
    </Box>
  );
};

// --- Indoor temperature display ---

const IndoorTemperatureDisplay = ({
  indoorTemperature,
}: {
  indoorTemperature: IndoorTemperatureSummary;
}) => {
  const { t } = useTranslation(["propai_properties"]);
  const hasValidTemp =
    indoorTemperature.averageTemperature != null &&
    !Number.isNaN(indoorTemperature.averageTemperature);

  return (
    <Grid
      container
      sx={{ position: "absolute", top: "120px", left: "860px", width: "320px" }}
      direction="row"
      alignItems="center"
      spacing={2}
    >
      <Grid item xs={6}>
        <Typography variant="body2" display="inline" pr={1}>
          {indoorTemperature.sensorCount}
        </Typography>
        <Typography variant="body1" display="inline">
          {t("sensors")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography
          variant="h2"
          display="inline"
          pr={1}
          color={hasValidTemp ? "tertiary.main" : "text.secondary"}
        >
          {hasValidTemp ? indoorTemperature.averageTemperature!.toFixed(1) : "N/A"}
        </Typography>
        <Typography
          variant="h4"
          display="inline"
          color={hasValidTemp ? "tertiary.main" : "text.secondary"}
        >
          °C
        </Typography>
        <Typography variant="body1">
          {t("current_average_indoor_temperature")}
          {!hasValidTemp && (
            <Typography variant="caption" display="block" color="text.secondary">
              No recent data available
            </Typography>
          )}
        </Typography>
      </Grid>
    </Grid>
  );
};

// --- Main component ---

type HeatingSystemDiagramProps = Readonly<{
  /** Pre-fetched sensor overlays for the diagram */
  overlays: DiagramSensorOverlay[];
  /** Indoor temperature summary */
  indoorTemperature: IndoorTemperatureSummary;
  /** Whether the data is still loading */
  isLoading?: boolean;
}>;

export const HeatingSystemDiagram: React.FC<HeatingSystemDiagramProps> = ({
  overlays,
  indoorTemperature,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Box
        p={2}
        width="100%"
        justifyContent="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <LinearProgress sx={{ width: "100%", mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading heating system data...
        </Typography>
      </Box>
    );
  }

  if (overlays.length === 0) {
    return null;
  }

  return (
    <Box p={2} width="100%" justifyContent="center" display="flex">
      <Box
        sx={{
          position: "relative",
          overflow: "auto",
          minWidth: "100%",
          textAlign: "center",
        }}
      >
        <Box sx={{ position: "relative", display: "inline-block", left: "-200px" }}>
          <HeatingDiagram />

          {/* Sensor overlays with temperature data */}
          {overlays.map((overlay) => (
            <SensorDataElement key={`${overlay.key}-${overlay.sensor.uid}`} sensorData={overlay} />
          ))}

          <IndoorTemperatureDisplay indoorTemperature={indoorTemperature} />
        </Box>
      </Box>
    </Box>
  );
};
